// src/components/RPA/buttons/UploadSupportingDocumentButton.js
// Single reusable upload button for all 23 supporting document types.
// Auditor selects the doc type from a dropdown, picks a file, and uploads.
// Shows Azure extraction results inline after upload.

import React, { useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API || 'http://localhost:8000';

// Mirrors the backend REGISTRY — display names and grouping for the dropdown
const DOC_TYPE_OPTIONS = [
  { group: 'Financial Statements',
    options: [
      { value: 'cash_flow_statement',          label: 'Cash Flow Statement' },
      { value: 'statement_of_changes_equity',  label: 'Statement of Changes in Equity' },
      { value: 'notes_to_financial_statements',label: 'Notes to Financial Statements' },
      { value: 'prior_year_financials',         label: 'Prior Year Financial Statements' },
    ]
  },
  { group: 'Ledgers & Schedules',
    options: [
      { value: 'general_ledger',               label: 'General Ledger' },
      { value: 'fixed_asset_register',         label: 'Fixed Asset Register' },
      { value: 'depreciation_schedules',       label: 'Depreciation Schedules' },
      { value: 'supporting_schedules',         label: 'Supporting Schedules' },
      { value: 'inventory_valuation_schedules',label: 'Inventory Valuation Schedules' },
    ]
  },
  { group: 'Receivables & Payables',
    options: [
      { value: 'ar_aging_report',              label: 'AR Aging Report' },
      { value: 'vendor_statements',            label: 'Vendor Statements' },
    ]
  },
  { group: 'Debt & Agreements',
    options: [
      { value: 'debt_confirmations',           label: 'Debt Confirmations' },
      { value: 'loan_agreements',              label: 'Loan Agreements' },
      { value: 'lease_agreements',             label: 'Lease Agreements' },
      { value: 'contracts',                    label: 'Contracts' },
    ]
  },
  { group: 'Legal & Compliance',
    options: [
      { value: 'legal_documents',              label: 'Legal Documents' },
      { value: 'title_documents',              label: 'Title Documents' },
      { value: 'regulatory_filings',           label: 'Regulatory Filings' },
      { value: 'tax_returns',                  label: 'Tax Returns' },
      { value: 'management_representation_letter', label: 'Management Representation Letter' },
    ]
  },
  { group: 'Analysis & Other',
    options: [
      { value: 'budget_documents',             label: 'Budget Documents' },
      { value: 'industry_benchmarks',          label: 'Industry Benchmarks' },
      { value: 'additional_evidence',          label: 'Additional Evidence' },
    ]
  },
];

// Flatten for easy label lookup
const ALL_OPTIONS = DOC_TYPE_OPTIONS.flatMap(g => g.options);
const getLabel = (value) => ALL_OPTIONS.find(o => o.value === value)?.label || value;

// ─── Styles ──────────────────────────────────────────────────────────────────
const s = {
  btn: {
    background: '#0891b2', color: '#fff', border: 'none',
    padding: '4px 10px', borderRadius: 4, fontSize: 11,
    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
  },
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
    zIndex: 9999, display: 'flex', alignItems: 'flex-start',
    justifyContent: 'center', overflowY: 'auto', padding: '32px 16px',
  },
  modal: {
    background: '#fff', borderRadius: 12, width: '100%', maxWidth: 620,
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '16px 20px', borderBottom: '1px solid #e5e7eb',
  },
  body: { padding: '20px' },
  label: { display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 },
  select: {
    width: '100%', padding: '8px 10px', border: '1px solid #d1d5db',
    borderRadius: 6, fontSize: 13, background: '#fff', cursor: 'pointer',
    boxSizing: 'border-box',
  },
  fileInput: {
    width: '100%', padding: '8px 10px', border: '1px dashed #d1d5db',
    borderRadius: 6, fontSize: 12, boxSizing: 'border-box', cursor: 'pointer',
    background: '#f9fafb',
  },
  uploadBtn: (disabled) => ({
    width: '100%', padding: '10px', marginTop: 16,
    background: disabled ? '#9ca3af' : '#0891b2',
    color: '#fff', border: 'none', borderRadius: 6, fontSize: 13,
    fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer',
  }),
  errorBox: {
    background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b',
    padding: '10px 12px', borderRadius: 6, marginTop: 12, fontSize: 12,
  },
  resultCard: {
    background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8,
    padding: 14, marginTop: 14,
  },
  flagCard: {
    background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6,
    padding: 10, marginTop: 8,
  },
  badge: (color) => ({
    display: 'inline-block', padding: '2px 8px', borderRadius: 999,
    fontSize: 10, fontWeight: 600,
    background: color === 'green' ? '#d1fae5' : color === 'yellow' ? '#fef3c7' : '#fee2e2',
    color: color === 'green' ? '#065f46' : color === 'yellow' ? '#92400e' : '#991b1b',
    border: `1px solid ${color === 'green' ? '#6ee7b7' : color === 'yellow' ? '#fde68a' : '#fca5a5'}`,
  }),
};

// ─── Result Renderer ─────────────────────────────────────────────────────────
const AnalysisResult = ({ result, onClose }) => {
  if (!result) return null;
  const statusColor = result.extractionStatus === 'success' ? 'green'
    : result.extractionStatus === 'partial' ? 'yellow' : 'red';

  return (
    <div style={s.resultCard}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ fontWeight: 700, fontSize: 14, color: '#111827' }}>
          ✅ {result.displayName} — Analysis Complete
        </div>
        <span style={s.badge(statusColor)}>
          {result.extractionStatus?.replace(/_/g, ' ')}
        </span>
      </div>

      {/* Summary */}
      {result.summary && Object.keys(result.summary).length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Summary</div>
          <div style={{ background: '#fff', border: '1px solid #d1fae5', borderRadius: 4, padding: 8 }}>
            {Object.entries(result.summary).map(([k, v]) => (
              <div key={k} style={{ display: 'flex', gap: 8, fontSize: 11, marginBottom: 2 }}>
                <span style={{ color: '#6b7280', minWidth: 180 }}>{k.replace(/_/g, ' ')}:</span>
                <span style={{ color: '#111827', fontWeight: 500 }}>{String(v ?? '—')}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Key Findings */}
      {result.keyFindings?.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Key Findings</div>
          {result.keyFindings.map((f, i) => (
            <div key={i} style={{ fontSize: 11, color: '#374151', padding: '3px 0', borderBottom: '1px solid #e5e7eb' }}>
              • {f}
            </div>
          ))}
        </div>
      )}

      {/* Audit Flags */}
      {result.auditFlags?.length > 0 && (
        <div style={s.flagCard}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#991b1b', marginBottom: 6 }}>
            ⚠️ Audit Flags ({result.auditFlags.length})
          </div>
          {result.auditFlags.map((f, i) => (
            <div key={i} style={{ fontSize: 11, color: '#7f1d1d', padding: '2px 0' }}>• {f}</div>
          ))}
        </div>
      )}

      {/* Completeness */}
      {result.completeness && (
        <div style={{ marginTop: 8, fontSize: 10, color: '#6b7280', fontStyle: 'italic' }}>
          Completeness: {result.completeness.score}/100 — {result.completeness.notes}
        </div>
      )}

      <button
        onClick={onClose}
        style={{ marginTop: 12, padding: '6px 14px', background: '#0891b2', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 500 }}
      >
        Done
      </button>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const UploadSupportingDocumentButton = ({ docId, onDone }) => {
  const [open,     setOpen]     = useState(false);
  const [docType,  setDocType]  = useState('');
  const [file,     setFile]     = useState(null);
  const [uploading,setUploading]= useState(false);
  const [error,    setError]    = useState('');
  const [result,   setResult]   = useState(null);

  const reset = () => {
    setDocType('');
    setFile(null);
    setError('');
    setResult(null);
  };

  const handleClose = () => {
    setOpen(false);
    reset();
  };

  const handleUpload = async () => {
    if (!docType || !file) return;
    setUploading(true);
    setError('');
    setResult(null);

    const form = new FormData();
    form.append('supportingDocument', file);

    try {
      const res = await axios.post(
        `${API_URL}/admin/rpa/docs/${docId}/supporting/${docType}`,
        form,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setResult(res.data.analysisResult);
      if (onDone) onDone();
    } catch (e) {
      setError(e?.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const canUpload = !!docType && !!file && !uploading;

  return (
    <>
      <button style={s.btn} onClick={() => setOpen(true)} title="Upload Supporting Documents">
        📎 Supporting Docs
      </button>

      {open && (
        <div style={s.overlay} onClick={e => { if (e.target === e.currentTarget) handleClose(); }}>
          <div style={s.modal}>
            {/* Header */}
            <div style={s.header}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>
                  📎 Upload Supporting Document
                </div>
                <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                  Azure will extract and analyze the document content for the selected type
                </div>
              </div>
              <button
                onClick={handleClose}
                style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#6b7280' }}
              >
                ✕
              </button>
            </div>

            <div style={s.body}>
              {!result ? (
                <>
                  {/* Doc Type selector */}
                  <div style={{ marginBottom: 14 }}>
                    <label style={s.label}>
                      Document Type <span style={{ color: '#dc2626' }}>*</span>
                    </label>
                    <select
                      style={s.select}
                      value={docType}
                      onChange={e => { setDocType(e.target.value); setError(''); }}
                    >
                      <option value="">— Select document type —</option>
                      {DOC_TYPE_OPTIONS.map(group => (
                        <optgroup key={group.group} label={group.group}>
                          {group.options.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>

                  {/* File picker */}
                  <div style={{ marginBottom: 4 }}>
                    <label style={s.label}>
                      File <span style={{ color: '#dc2626' }}>*</span>
                      <span style={{ fontWeight: 400, color: '#6b7280', marginLeft: 6 }}>
                        (CSV, XLSX, PDF, or TXT — max 50 MB)
                      </span>
                    </label>
                    <input
                      type="file"
                      accept=".csv,.xlsx,.xls,.pdf,.txt,.doc,.docx"
                      style={s.fileInput}
                      onChange={e => { setFile(e.target.files[0] || null); setError(''); }}
                    />
                  </div>

                  {file && (
                    <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>
                      Selected: {file.name} ({(file.size / 1024).toFixed(1)} KB)
                    </div>
                  )}

                  {error && <div style={s.errorBox}>{error}</div>}

                  <button
                    onClick={handleUpload}
                    disabled={!canUpload}
                    style={s.uploadBtn(!canUpload)}
                  >
                    {uploading
                      ? `⏳ Uploading and analyzing ${docType ? getLabel(docType) : ''}…`
                      : `📤 Upload & Analyze${docType ? ` — ${getLabel(docType)}` : ''}`
                    }
                  </button>

                  {uploading && (
                    <div style={{ fontSize: 11, color: '#6b7280', textAlign: 'center', marginTop: 8 }}>
                      Azure is extracting and analyzing your document. This may take 15–30 seconds.
                    </div>
                  )}
                </>
              ) : (
                <AnalysisResult result={result} onClose={handleClose} />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UploadSupportingDocumentButton;